import { Fragment, useEffect, useRef, useState } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocContactQR,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DocSignatures,
  type DocSignature,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import { TraumaAppendix } from './TraumaAppendix';
import { MapPickerButton } from '../../components/map';
import {
  ContactPickerButton,
  CompanyPickerButton,
  SaveCompanyButton,
  type ContactPerson,
  type CompanyPickResult,
  type AresPickResult,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useSaveDocument } from '../../api/hooks';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Traumatologický plán — "živý dokument" pro BOZP.
 * Struktura převzatá z původní šablony (frontend/index.html, tab-trauma):
 *   01 Identifikace stavby a odpovědných osob
 *   02 Nouzové kontakty (IZS)
 *   03 Postup při pracovním úrazu
 *   04 Umístění lékárničky a záchranného vybavení
 *   05 Zákonné povinnosti
 *   06 Zodpovědné osoby (podpisy)
 *   07 Lékařská zařízení v okolí
 */

interface KrokItem {
  id: string;
  text: string;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function deriveInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w && /[\p{L}]/u.test(w[0]) && !/^(s\.?r\.?o\.?|a\.?s\.?|spol|ltd|inc)$/i.test(w));
  if (words.length === 0) return '—';
  return words
    .slice(0, 3)
    .map((w) => w[0].toLocaleUpperCase('cs'))
    .join('');
}

export function TraumaPage() {
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const [stavba, setStavba] = useState({
    nazev: '',
    adresa: '',
    cPovoleni: '',
  });
  const [zakazka, setZakazka] = useState('');
  const [datumAktualizace, setDatumAktualizace] = useState('');

  const [zhotovitel, setZhotovitel] = useState({
    firma: '',
    ico: '',
    sidlo: '',
  });

  const [kontakty, setKontakty] = useState({
    izs: '112',
    zachranka: '155',
    hasici: '150',
    policie: '158',
    mestskaPolicie: '156',
  });

  const [kroky, setKroky] = useState<KrokItem[]>([
    { id: newId('k'), text: 'Zastavte práci — nepřesouvejte zraněného, pokud to není nutné k zajištění bezpečnosti.' },
    { id: newId('k'), text: 'Přivolejte pomoc — zavolejte záchrannou zdravotní službu (155) nebo integrovaný záchranný systém (112).' },
    { id: newId('k'), text: 'Poskytněte první pomoc — postupujte dle školení BOZP, použijte lékárničku na staveništi.' },
    { id: newId('k'), text: 'Uvědomte stavbyvedoucího — ihned informujte odpovědnou osobu na staveništi.' },
    { id: newId('k'), text: 'Zajistěte místo úrazu — neměňte stav místa do příchodu záchranářů a šetření.' },
    { id: newId('k'), text: 'Zapište úraz — proveďte zápis do Knihy úrazů co nejdříve po události.' },
  ]);

  const [lekarnicka, setLekarnicka] = useState(
    'Lékárnička první pomoci je umístěna na staveništi na viditelném a přístupném místě (označeno červeným křížem). ' +
      'Záchranná přikrývka a základní záchranné prostředky jsou k dispozici u stavbymistra / v buňce stavbyvedoucího. ' +
      'Defibrilátor (AED): dle pokynů koordinátora BOZP.',
  );

  const [zakonnePovinnosti, setZakonnePovinnosti] = useState(
    'Dle § 105 zákoníku práce (č. 262/2006 Sb.) je zaměstnavatel povinen evidovat pracovní úrazy v Knize úrazů. ' +
      'Při úrazu s pracovní neschopností delší než 3 dny nebo při smrtelném úrazu je zaměstnavatel povinen vyhotovit ' +
      'Záznam o úrazu a nahlásit jej příslušným orgánům (oblastní inspektorát práce, pojišťovny, Policie ČR apod.).',
  );

  const [osoby, setOsoby] = useState({
    stavbyvedouci: { jmeno: '', tel: '', email: '' },
    bozpKoordinator: {
      jmeno: '',
      tel: '',
      email: '',
    },
  });

  const [zarizeni, setZarizeni] = useState({
    nemocnice: '',
    pohotovost: '24/7',
    telefon: '',
    lekar: '',
  });

  const [podpisy, setPodpisy] = useState<{ sv: string; bozp: string }>({ sv: '', bozp: '' });

  // Skryté standardní řádky + vlastní řádky v hlavičkových sekcích.
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  const setStavbaPatch = (patch: Partial<typeof stavba>) =>
    setStavba((s) => ({ ...s, ...patch }));
  const setZhotovitelPatch = (patch: Partial<typeof zhotovitel>) =>
    setZhotovitel((z) => ({ ...z, ...patch }));
  const setKontaktyPatch = (patch: Partial<typeof kontakty>) =>
    setKontakty((k) => ({ ...k, ...patch }));
  const setZarizeniPatch = (patch: Partial<typeof zarizeni>) =>
    setZarizeni((z) => ({ ...z, ...patch }));

  const addKrok = () =>
    setKroky((list) => [...list, { id: newId('k'), text: '' }]);
  const updateKrok = (id: string, text: string) =>
    setKroky((list) => list.map((x) => (x.id === id ? { ...x, text } : x)));
  const removeKrok = (id: string) =>
    setKroky((list) => list.filter((x) => x.id !== id));

  /** Předvyplní formulář ze snímku dat (dokument z archivu i lokální koncept). */
  function applySnapshot(d: Record<string, unknown>) {
    if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<typeof stavba>) }));
    if (d.zhotovitel) setZhotovitel((z) => ({ ...z, ...(d.zhotovitel as Partial<typeof zhotovitel>) }));
    if (d.zakazka != null) setZakazka(String(d.zakazka));
    if (d.datumAktualizace != null) setDatumAktualizace(String(d.datumAktualizace));
    if (d.kontakty) setKontakty((k) => ({ ...k, ...(d.kontakty as Partial<typeof kontakty>) }));
    if (Array.isArray(d.kroky)) setKroky(d.kroky as KrokItem[]);
    if (d.osoby) setOsoby((o) => ({ ...o, ...(d.osoby as Partial<typeof osoby>) }));
    if (d.zarizeni) setZarizeni((z) => ({ ...z, ...(d.zarizeni as Partial<typeof zarizeni>) }));
    if (d.lekarnicka != null) setLekarnicka(String(d.lekarnicka));
    if (d.zakonnePovinnosti != null) setZakonnePovinnosti(String(d.zakonnePovinnosti));
    if (d.podpisy) setPodpisy((p) => ({ ...p, ...(d.podpisy as Partial<typeof podpisy>) }));
    rows.apply(d.radky);
  }

  const docData = {
    stavba, zhotovitel, zakazka, datumAktualizace, kontakty, kroky, osoby, zarizeni,
    lekarnicka, zakonnePovinnosti, podpisy,
    radky: rows.snapshot(),
  };
  const { editId } = useDocEditor(applySnapshot);

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového dokumentu) —
  // bez něj se při zavření/reloadu záložky ztratil celý rozepsaný formulář.
  const draft = useDocDraft<Record<string, unknown>>('trauma', editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stavba, zhotovitel, zakazka, datumAktualizace, kontakty, kroky, osoby, zarizeni, lekarnicka, zakonnePovinnosti, podpisy, rows]);

  /**
   * Uložení do archivu. Chybu je nutné ukázat — dřív se `mutateAsync` odmítlo
   * bez ošetření a uživatel si myslel, že je dokument uložený.
   */
  async function handleSave() {
    try {
      await saveDoc.mutateAsync({
        id: editId ?? undefined,
        doc_type: 'trauma',
        title: `Traumatologický plán — ${stavba.nazev || activeStavba?.nazev || 'stavba'}`,
        stavba: activeStavba?.nazev ?? stavba.nazev,
        data_json: docData,
      });
      draft.clear();
      toast.success('Traumatologický plán uložen do archivu.');
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
    }
  }

  const metaLine = [
    zakazka && `zakázka ${zakazka}`,
    datumAktualizace && `aktualizováno ${datumAktualizace}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const signatureItems: DocSignature[] = [
    {
      label: 'Stavbyvedoucí',
      name: osoby.stavbyvedouci.jmeno,
      role: 'Zhotovitel — stavbyvedoucí',
      image: podpisy.sv || undefined,
      onSign: (dataUrl) => setPodpisy((p) => ({ ...p, sv: dataUrl })),
    },
    {
      label: 'Koordinátor BOZP',
      name: osoby.bozpKoordinator.jmeno,
      role: 'Koordinátor bezpečnosti práce',
      image: podpisy.bozp || undefined,
      onSign: (dataUrl) => setPodpisy((p) => ({ ...p, bozp: dataUrl })),
    },
  ];

  return (
    <>
      <DocLayout
        breadcrumb={['BOZP', 'Trauma plán']}
        title="Traumatologický plán"
        statusText="Rozpracovaný koncept"
        status="draft"
        actions={
          <>
            <NewDocButton draftKey="trauma" path="/app/bozp/trauma" />
            <SavedDocsButton
              docType="trauma"
              title="Uložené traumatologické plány"
              currentId={editId}
              path="/app/bozp/trauma"
            />
            <Button variant="secondary" onClick={() => printToPdf('Trauma_plan.pdf')}>
              Tisk A4
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: deriveInitials(zhotovitel.firma),
              name: zhotovitel.firma || '—',
              address:
                [zhotovitel.sidlo, zhotovitel.ico ? `IČO ${zhotovitel.ico}` : '']
                  .filter(Boolean)
                  .join(' · ') || '—',
            }}
            tag="☣ Traumatologický plán"
            title={
              <>
                Traumatologický plán stavby
                {stavba.nazev && (
                  <>
                    {' '}
                    <span style={{ fontWeight: 400, fontSize: 14, color: 'var(--sd-doc-dim)' }}>
                      · {stavba.nazev}
                    </span>
                  </>
                )}
              </>
            }
            meta={metaLine || undefined}
            footer={{
              left: zhotovitel.firma || '—',
              center: 'str. 1 / 1',
              right: zakazka || '—',
            }}
          >
            <DocBlock
              idx="01"
              title="Identifikace stavby a odpovědných osob"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s01" />
                  <StavbaPickerButton
                    title="Načíst stavbu ze seznamu"
                    onPick={(p: StavbaPickResult) => {
                      setStavba({
                        nazev: p.nazev,
                        adresa: p.adresa,
                        cPovoleni: p.stavebni_povoleni,
                      });
                      if (p.zakazka_cislo) setZakazka(p.zakazka_cislo);
                    }}
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu"
                    build={() => ({
                      nazev: stavba.nazev,
                      adresa: stavba.adresa,
                      stavebni_povoleni: stavba.cPovoleni,
                      zakazka_cislo: zakazka,
                      datum_zahajeni: '',
                    })}
                  />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst zhotovitele z adresáře"
                    onPick={(c: CompanyPickResult) => {
                      setZhotovitel({ firma: c.nazev, ico: c.ico, sidlo: c.adresa });
                      setOsoby((o) => ({
                        ...o,
                        stavbyvedouci: {
                          jmeno: c.stavbyvedouci || o.stavbyvedouci.jmeno,
                          tel: c.kontakt_tel || o.stavbyvedouci.tel,
                          email: c.kontakt_email || o.stavbyvedouci.email,
                        },
                        bozpKoordinator: {
                          jmeno: c.bozp_koordinator || o.bozpKoordinator.jmeno,
                          tel: c.bozp_tel || o.bozpKoordinator.tel,
                          email: o.bozpKoordinator.email,
                        },
                      }));
                    }}
                  />
                  <SaveCompanyButton
                    title="Uložit zhotovitele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: zhotovitel.firma,
                      ico: zhotovitel.ico,
                      dic: '',
                      adresa: zhotovitel.sidlo,
                      stavbyvedouci: osoby.stavbyvedouci.jmeno,
                      kontakt_tel: osoby.stavbyvedouci.tel,
                      kontakt_email: osoby.stavbyvedouci.email,
                      bozp_koordinator: osoby.bozpKoordinator.jmeno,
                      bozp_tel: osoby.bozpKoordinator.tel,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s01.nazev') && (
                <DocRow
                  k="Název stavby"
                  value={stavba.nazev}
                  placeholder="např. RD Březník — etapa II"
                  onChange={(v) => setStavbaPatch({ nazev: v })}
                  onRemove={() => hide('s01.nazev')}
                />
              )}
              {!isHidden('s01.adresa') && (
                <DocRow
                  k="Adresa staveniště"
                  value={stavba.adresa}
                  placeholder="např. Březník 142, 675 74"
                  onChange={(v) => setStavbaPatch({ adresa: v })}
                  actions={
                    <MapPickerButton
                      value={stavba.adresa}
                      onApply={(v) => setStavbaPatch({ adresa: v })}
                      title="Adresa staveniště"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s01.adresa')}
                />
              )}
              {!isHidden('s01.zhotovitel') && (
                <DocRow
                  k="Zhotovitel"
                  v={
                    <DocInlineAresName
                      value={zhotovitel.firma}
                      onChange={(v) => setZhotovitelPatch({ firma: v })}
                      onPick={(d: AresPickResult) =>
                        setZhotovitelPatch({ firma: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                      placeholder="např. Stavby Novák s.r.o."
                    />
                  }
                  onRemove={() => hide('s01.zhotovitel')}
                />
              )}
              {!isHidden('s01.ico') && (
                <DocRow
                  k="IČO zhotovitele"
                  mono
                  value={zhotovitel.ico}
                  placeholder="12345678"
                  onChange={(v) => setZhotovitelPatch({ ico: v })}
                  actions={
                    <DocInlineAresLookup
                      ico={zhotovitel.ico}
                      onResult={(d: AresPickResult) =>
                        setZhotovitelPatch({
                          firma: d.nazev || zhotovitel.firma,
                          ico: d.ico,
                          sidlo: d.adresa || zhotovitel.sidlo,
                        })
                      }
                      title="Načíst zhotovitele z ARES podle IČO"
                    />
                  }
                  onRemove={() => hide('s01.ico')}
                />
              )}
              {!isHidden('s01.sidlo') && (
                <DocRow
                  k="Sídlo zhotovitele"
                  value={zhotovitel.sidlo}
                  placeholder="např. Lesní 22, 664 84 Zastávka"
                  onChange={(v) => setZhotovitelPatch({ sidlo: v })}
                  onRemove={() => hide('s01.sidlo')}
                />
              )}
              {!isHidden('s01.sv') && (
                <DocRow
                  k="Stavbyvedoucí"
                  value={osoby.stavbyvedouci.jmeno}
                  placeholder="jméno a příjmení"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      stavbyvedouci: { ...o.stavbyvedouci, jmeno: v },
                    }))
                  }
                  actions={
                    <ContactPickerButton
                      attachToCompany={zhotovitel.firma}
                      companyDraft={{ role: 'zhotovitel', ico: zhotovitel.ico, adresa: zhotovitel.sidlo }}
                      draftNew={{
                        jmeno: osoby.stavbyvedouci.jmeno,
                        funkce: 'stavbyvedoucí',
                        tel: osoby.stavbyvedouci.tel,
                        email: osoby.stavbyvedouci.email,
                      }}
                      title="Stavbyvedoucí — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setOsoby((o) => ({
                          ...o,
                          stavbyvedouci: {
                            jmeno: p.jmeno,
                            tel: p.tel || o.stavbyvedouci.tel,
                            email: p.email || o.stavbyvedouci.email,
                          },
                        }))
                      }
                    />
                  }
                  onRemove={() => hide('s01.sv')}
                />
              )}
              {!isHidden('s01.svTel') && (
                <DocRow
                  k="Tel. stavbyvedoucí"
                  mono
                  value={osoby.stavbyvedouci.tel}
                  placeholder="+420 …"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      stavbyvedouci: { ...o.stavbyvedouci, tel: v },
                    }))
                  }
                  inputType="tel"
                  onRemove={() => hide('s01.svTel')}
                />
              )}
              {!isHidden('s01.bozp') && (
                <DocRow
                  k="Koordinátor BOZP"
                  value={osoby.bozpKoordinator.jmeno}
                  placeholder="jméno a příjmení"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      bozpKoordinator: { ...o.bozpKoordinator, jmeno: v },
                    }))
                  }
                  actions={
                    <ContactPickerButton
                      attachToCompany={zhotovitel.firma}
                      companyDraft={{ role: 'zhotovitel', ico: zhotovitel.ico, adresa: zhotovitel.sidlo }}
                      draftNew={{
                        jmeno: osoby.bozpKoordinator.jmeno,
                        funkce: 'koordinátor BOZP',
                        tel: osoby.bozpKoordinator.tel,
                        email: osoby.bozpKoordinator.email,
                      }}
                      title="Koordinátor BOZP — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setOsoby((o) => ({
                          ...o,
                          bozpKoordinator: {
                            jmeno: p.jmeno,
                            tel: p.tel || o.bozpKoordinator.tel,
                            email: p.email || o.bozpKoordinator.email,
                          },
                        }))
                      }
                    />
                  }
                  onRemove={() => hide('s01.bozp')}
                />
              )}
              {!isHidden('s01.bozpTel') && (
                <DocRow
                  k="Tel. koordinátor BOZP"
                  mono
                  value={osoby.bozpKoordinator.tel}
                  placeholder="+420 …"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      bozpKoordinator: { ...o.bozpKoordinator, tel: v },
                    }))
                  }
                  inputType="tel"
                  onRemove={() => hide('s01.bozpTel')}
                />
              )}
              {!isHidden('s01.nemocnice') && (
                <DocRow
                  k="Nejbližší nemocnice"
                  value={zarizeni.nemocnice}
                  placeholder="např. Nemocnice Třebíč, Purkyňovo nám. 2"
                  onChange={(v) => setZarizeniPatch({ nemocnice: v })}
                  actions={
                    <MapPickerButton
                      value={zarizeni.nemocnice}
                      onApply={(v) => setZarizeniPatch({ nemocnice: v })}
                      title="Adresa nemocnice"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s01.nemocnice')}
                />
              )}
              {!isHidden('s01.lekar') && (
                <DocRow
                  k="Lékař / záchrana"
                  value={zarizeni.lekar}
                  placeholder="jméno — závodní lékař"
                  onChange={(v) => setZarizeniPatch({ lekar: v })}
                  onRemove={() => hide('s01.lekar')}
                />
              )}
              {!isHidden('s01.zakazka') && (
                <DocRow
                  k="Zakázka"
                  mono
                  value={zakazka}
                  placeholder="např. Z-2026/0412"
                  onChange={setZakazka}
                  onRemove={() => hide('s01.zakazka')}
                />
              )}
              {!isHidden('s01.aktualizace') && (
                <DocRow
                  k="Datum aktualizace"
                  mono
                  value={datumAktualizace}
                  onChange={setDatumAktualizace}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={datumAktualizace}
                      onChange={setDatumAktualizace}
                    />
                  }
                  onRemove={() => hide('s01.aktualizace')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Nouzové kontakty (IZS)"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={<DocAddRowButton rows={rows} section="s02" />}
            >
              {!isHidden('s02.izs') && (
                <DocRow
                  k="Tísňové volání (IZS)"
                  mono
                  value={kontakty.izs}
                  onChange={(v) => setKontaktyPatch({ izs: v })}
                  inputType="tel"
                  onRemove={() => hide('s02.izs')}
                />
              )}
              {!isHidden('s02.zachranka') && (
                <DocRow
                  k="Zdravotnická záchranka"
                  mono
                  value={kontakty.zachranka}
                  onChange={(v) => setKontaktyPatch({ zachranka: v })}
                  inputType="tel"
                  onRemove={() => hide('s02.zachranka')}
                />
              )}
              {!isHidden('s02.hasici') && (
                <DocRow
                  k="Hasičský záchranný sbor"
                  mono
                  value={kontakty.hasici}
                  onChange={(v) => setKontaktyPatch({ hasici: v })}
                  inputType="tel"
                  onRemove={() => hide('s02.hasici')}
                />
              )}
              {!isHidden('s02.policie') && (
                <DocRow
                  k="Policie ČR"
                  mono
                  value={kontakty.policie}
                  onChange={(v) => setKontaktyPatch({ policie: v })}
                  inputType="tel"
                  onRemove={() => hide('s02.policie')}
                />
              )}
              {!isHidden('s02.mp') && (
                <DocRow
                  k="Městská policie"
                  mono
                  value={kontakty.mestskaPolicie}
                  onChange={(v) => setKontaktyPatch({ mestskaPolicie: v })}
                  inputType="tel"
                  onRemove={() => hide('s02.mp')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Postup při pracovním úrazu"
              actions={
                <DocActionButton onClick={addKrok} title="Přidat krok">
                  + Přidat krok
                </DocActionButton>
              }
            >
              {kroky.map((k, i) => (
                <DocRow
                  key={k.id}
                  k={`${i + 1}. krok`}
                  multiline
                  value={k.text}
                  onChange={(v) => updateKrok(k.id, v)}
                  placeholder="Popište krok…"
                  actions={
                    <DocActionButton
                      onClick={() => removeKrok(k.id)}
                      title="Odebrat krok"
                    >
                      ✕
                    </DocActionButton>
                  }
                />
              ))}
            </DocBlock>

            <DocBlock idx="04" title="Umístění lékárničky a záchranného vybavení">
              <DocRow
                k="Popis umístění"
                multiline
                value={lekarnicka}
                onChange={setLekarnicka}
                placeholder="Kde je lékárnička, AED, izolační přikrývka…"
              />
            </DocBlock>

            <DocBlock idx="05" title="Zákonné povinnosti">
              <DocRow
                k="§ 105 zákoníku práce"
                multiline
                value={zakonnePovinnosti}
                onChange={setZakonnePovinnosti}
                placeholder="Povinnosti dle § 105 zákona č. 262/2006 Sb., NV č. 201/2010 Sb…"
              />
            </DocBlock>

            <DocBlock idx="06" title="Zodpovědné osoby (kontaktní údaje)">
              <Fragment>
                <DocRow
                  k="Stavbyvedoucí — jméno"
                  value={osoby.stavbyvedouci.jmeno}
                  placeholder="jméno a příjmení"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      stavbyvedouci: { ...o.stavbyvedouci, jmeno: v },
                    }))
                  }
                  actions={
                    <ContactPickerButton
                      attachToCompany={zhotovitel.firma}
                      companyDraft={{ role: 'zhotovitel', ico: zhotovitel.ico, adresa: zhotovitel.sidlo }}
                      draftNew={{
                        jmeno: osoby.stavbyvedouci.jmeno,
                        funkce: 'stavbyvedoucí',
                        tel: osoby.stavbyvedouci.tel,
                        email: osoby.stavbyvedouci.email,
                      }}
                      title="Stavbyvedoucí — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setOsoby((o) => ({
                          ...o,
                          stavbyvedouci: {
                            jmeno: p.jmeno,
                            tel: p.tel || o.stavbyvedouci.tel,
                            email: p.email || o.stavbyvedouci.email,
                          },
                        }))
                      }
                    />
                  }
                />
                <DocRow
                  k="Stavbyvedoucí — telefon"
                  mono
                  value={osoby.stavbyvedouci.tel}
                  placeholder="+420 …"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      stavbyvedouci: { ...o.stavbyvedouci, tel: v },
                    }))
                  }
                  inputType="tel"
                />
                <DocRow
                  k="Stavbyvedoucí — e-mail"
                  mono
                  value={osoby.stavbyvedouci.email}
                  placeholder="email@…"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      stavbyvedouci: { ...o.stavbyvedouci, email: v },
                    }))
                  }
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={osoby.stavbyvedouci.jmeno}
                      role="Stavbyvedoucí"
                      phone={osoby.stavbyvedouci.tel}
                      email={osoby.stavbyvedouci.email}
                      title="QR vizitka — stavbyvedoucí"
                    />
                  }
                />
              </Fragment>
              <Fragment>
                <DocRow
                  k="BOZP koordinátor — jméno"
                  value={osoby.bozpKoordinator.jmeno}
                  placeholder="jméno a příjmení"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      bozpKoordinator: { ...o.bozpKoordinator, jmeno: v },
                    }))
                  }
                  actions={
                    <ContactPickerButton
                      attachToCompany={zhotovitel.firma}
                      companyDraft={{ role: 'zhotovitel', ico: zhotovitel.ico, adresa: zhotovitel.sidlo }}
                      draftNew={{
                        jmeno: osoby.bozpKoordinator.jmeno,
                        funkce: 'koordinátor BOZP',
                        tel: osoby.bozpKoordinator.tel,
                        email: osoby.bozpKoordinator.email,
                      }}
                      title="Koordinátor BOZP — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setOsoby((o) => ({
                          ...o,
                          bozpKoordinator: {
                            jmeno: p.jmeno,
                            tel: p.tel || o.bozpKoordinator.tel,
                            email: p.email || o.bozpKoordinator.email,
                          },
                        }))
                      }
                    />
                  }
                />
                <DocRow
                  k="BOZP koordinátor — telefon"
                  mono
                  value={osoby.bozpKoordinator.tel}
                  placeholder="+420 …"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      bozpKoordinator: { ...o.bozpKoordinator, tel: v },
                    }))
                  }
                  inputType="tel"
                />
                <DocRow
                  k="BOZP koordinátor — e-mail"
                  mono
                  value={osoby.bozpKoordinator.email}
                  placeholder="email@…"
                  onChange={(v) =>
                    setOsoby((o) => ({
                      ...o,
                      bozpKoordinator: { ...o.bozpKoordinator, email: v },
                    }))
                  }
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={osoby.bozpKoordinator.jmeno}
                      role="BOZP koordinátor"
                      phone={osoby.bozpKoordinator.tel}
                      email={osoby.bozpKoordinator.email}
                      title="QR vizitka — BOZP koordinátor"
                    />
                  }
                />
              </Fragment>
            </DocBlock>

            <DocBlock
              idx="07"
              title="Lékařská zařízení v okolí"
              hiddenCount={hiddenCount('s07')}
              onRestoreHidden={() => restoreSection('s07')}
              actions={<DocAddRowButton rows={rows} section="s07" />}
            >
              {!isHidden('s07.nemocnice') && (
                <DocRow
                  k="Nejbližší nemocnice"
                  value={zarizeni.nemocnice}
                  placeholder="např. Nemocnice Třebíč, Purkyňovo nám. 2"
                  onChange={(v) => setZarizeniPatch({ nemocnice: v })}
                  actions={
                    <MapPickerButton
                      value={zarizeni.nemocnice}
                      onApply={(v) => setZarizeniPatch({ nemocnice: v })}
                      title="Adresa nemocnice"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s07.nemocnice')}
                />
              )}
              {!isHidden('s07.pohotovost') && (
                <DocRow
                  k="Pohotovost"
                  value={zarizeni.pohotovost}
                  onChange={(v) => setZarizeniPatch({ pohotovost: v })}
                  onRemove={() => hide('s07.pohotovost')}
                />
              )}
              {!isHidden('s07.telefon') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={zarizeni.telefon}
                  placeholder="+420 …"
                  onChange={(v) => setZarizeniPatch({ telefon: v })}
                  inputType="tel"
                  onRemove={() => hide('s07.telefon')}
                />
              )}
              <DocCustomRows rows={rows} section="s07" />
            </DocBlock>

            <DocSignatures items={signatureItems} />

            {/* Příloha (str. 2–26) – jako pokračování stejného dokumentu */}
            <TraumaAppendix />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="trauma"
        docTitle={`Traumatologický plán — ${stavba.nazev || 'stavba'}`}
        defaultTo={osoby.bozpKoordinator.email}
        defaultCc={osoby.stavbyvedouci.email}
        defaultSubject={`Traumatologický plán — ${stavba.nazev || 'stavba'}`}
        meta={`Stavba: ${stavba.nazev || '—'}${
          activeStavba?.nazev && activeStavba.nazev !== stavba.nazev
            ? ' · ' + activeStavba.nazev
            : ''
        }`}
      />
    </>
  );
}
